import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';

import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {debounceTime, Subject, takeUntil} from 'rxjs';

import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef,} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatSelectModule} from '@angular/material/select';
import {ConfirmationModalComponent} from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import {IPaginationValue, PaginationComponent,} from '../../../shared/components/pagination/pagination.component';
import {TableComponent} from '../../../shared/components/table/table.component';
import {AlertService} from '../../../shared/services/alert.service';
import {AllowedDirective} from '../../../shared/services/directives/allowed.directive';
import {LoadingService} from '../../../shared/services/loading.service';
import {PollService} from '../../../shared/services/poll.service';
import {IPollToList} from '../../../utils/interfaces/poll';
import {ITableHeader} from '../../../utils/interfaces/table-header';
import {PreviewPollComponent} from '../preview-poll/preview-poll.component';
import {PollTypeEnum} from "../../../utils/enums/poll-type.enum";
import { PollTypeDescriptionPipe } from '../../../shared/pipes/poll-type-description.pipe';

@Component({
  selector: 'app-list',
  standalone: true,
  imports: [
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    TableComponent,
    PaginationComponent,
    AllowedDirective
  ],
  templateUrl: './list.component.html',
  styleUrl: './list.component.scss',
})
export class ListComponent implements OnInit, OnDestroy {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly pollService = inject(PollService);
  private readonly loadingService = inject(LoadingService);
  private readonly alertService = inject(AlertService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly unsubscribe$ = new Subject();

  tableHeader: ITableHeader[] = [];

  list: IPollToList[] = [];

  actionList: string[] = [
    'Duplicar',
    'Visualizar',
    'Visualizar clientes',
    'Editar',
    'Excluir',
  ];

  countPoll = 0;
  pagination: IPaginationValue = {
    page: 1,
    take: 10,
  };

  searchString = new FormControl<string>('');
  searchStatus = new FormControl<boolean | null>(null);

  ngOnInit(): void {
    this.tableHeader = [
      'Status,status',
      'Tipo de pesquisas,typeDescription',
      'Nome da pesquisas,title',
      'Empresa,company',
      'N respostas,submissionCount',
      'Última avaliação,lastSubmission',
      'Ações',
    ].map((item) => {
      const [description, propertyKey] = item.split(',');

      return {
        description: description,
        propertyKey: propertyKey ?? '',
        status: propertyKey === 'status',
        date: propertyKey === 'lastSubmission',
        actions: description === 'Ações',
        filter: false,
        showHour: false,
      };
    });

    const {count, data} = this.activatedRoute.snapshot.data['polls'];
    this.countPoll = count ?? 0;
    this.pollHandlerList(data);

    this.searchString.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .pipe(debounceTime(700))
      .subscribe(() => {
        this.loadingService.showLoading();
        this.getPolls(true);
      });

    this.searchStatus.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.loadingService.showLoading();
        this.getPolls(true);
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  changeStatus(id: string): void {
    this.loadingService.showLoading();

    this.pollService
      .updatePollStatus(id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.loadingService.closeLoading();
        this.alertService.success('Status da pesquisa alterado com sucesso');
      });
  }

  selectAction(item: { id: string; func: string }) {
    if (item.func === 'Duplicar') {
      this.duplicatePoll(item.id);
    } else if (item.func === 'Visualizar') {
      this.viewPoll(item.id);
    } else if (item.func === 'Visualizar clientes') {
      this.customersHandler(item.id);
    } else if (item.func === 'Editar') {
      this.editPoll(item.id);
    } else if (item.func === 'Excluir') {
      this.handlerDeletePoll(item.id);
    }
  }

  duplicatePoll(id: string): void {
    this.loadingService.showLoading();

    this.pollService
      .duplicatePoll(id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        this.loadingService.closeLoading();
        this.alertService.success('Pesquisa duplicada com sucesso');

        void this.router.navigate(['main/polls/edit-poll', res.data, {duplicate: true}]);
      });
  }

  editPoll(id: string): void {
    void this.router.navigate(['main/polls/edit-poll', id, {edit: true}]);
  }

  onPaginationChange(event: IPaginationValue): void {
    this.pagination = event;

    this.loadingService.showLoading();
    this.getPolls();
  }

  getPolls(search = false): void {
    this.pollService
      .getPolls(
        search,
        this.searchString.value ?? '',
        this.pagination.page,
        this.pagination.take,
        this.searchStatus.value
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        this.pollHandlerList(res.data);
        this.countPoll = res.count ?? 0;
        this.loadingService.closeLoading();

        if (search) {
          this.pagination.page = 1;
        }
      });
  }

  customersHandler(id: string): void {
    const poll = this.list.find((poll) => poll.id === id);

    this.dialog.open(ListCustomersComponent, {
      minWidth: 600,
      maxWidth: 600,
      data: {
        poll,
      },
    });
  }

  handlerDeletePoll(id: string) {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '500px',
      data: {
        title: 'Excluir pesquisa',
        description: 'Tem certeza que deseja excluir essa pesquisa?',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result) this.deletePoll(id);
      });
  }

  deletePoll(id: string): void {
    this.loadingService.showLoading();

    this.pollService
      .deletePoll(id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.getPolls();

        this.alertService.success('Pesquisa excluída com sucesso');
      });
  }

  viewPoll(id: string): void {
    this.loadingService.showLoading();

    this.pollService
      .getPollById(id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        this.loadingService.closeLoading();
        this.dialog.open(PreviewPollComponent, {
          minWidth: 800,
          maxWidth: 800,
          data: {
            formValue: res.data,
          },
        });
      });
  }

  private pollHandlerList(pollInput: IPollToList[]) {
  const pollTypeDescriptionPipe = new PollTypeDescriptionPipe();
  this.list = pollInput.map((poll) => ({
    ...poll,
    typeDescription: pollTypeDescriptionPipe.transform(poll.type),
  }));
}

}

@Component({
  selector: 'app-list',
  standalone: true,
  imports: [MatIconModule, MatDialogModule],
  template: `
    <div class="dialog-title" mat-dialog-title>
      <p>Clientes com a perquisa: {{ data.poll.title }}</p>
      <mat-icon mat-dialog-close>close</mat-icon>
    </div>
    <mat-dialog-content class="mat-typography">
      @for (item of data.poll.customerPoll; track $index) {
        <p>{{ item.name }}</p>
      } @empty {
        <p class="empty">Não há clientes para está pesquisa</p>
      }
    </mat-dialog-content>
  `,
  styles: [
    `
      .dialog-title {
        display: flex;
        justify-content: space-between;
        align-items: center;

        p {
          font-weight: 600;
          width: 100%;
          font-size: 1rem;
        }

        mat-icon {
          cursor: pointer;
        }

        border-bottom: 1px solid var(--s-high-medium);

        margin-bottom: 1rem;
      }
    `,
  ],
})
export class ListCustomersComponent {
  readonly dialogRef = inject(MatDialogRef<ListCustomersComponent>);
  readonly data = inject<{
    poll: IPollToList;
  }>(MAT_DIALOG_DATA);
}
